import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment-timezone";
import { Subject } from "rxjs";
import {
  debounceTime,
  filter,
  first,
  startWith,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import {
  FlowPayloadProperty,
  PayloadLookup,
} from "src/app/flows/flow/phone-node/phone-node-menu/phone-node-menu.component";
import { FlowsService } from "src/app/flows/flows.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { SignalPreviewDialogComponent } from "src/app/shared/dialogs/signal-preview-dialog/signal-preview-dialog.component";
import { SignalsService } from "src/app/signals/signals.service";
import { TitleService } from "src/app/title.service";
import { UiService } from "src/app/ui/ui.service";
import { BroadcastService } from "../broadcast.service";
import { isNumber, isObject, find } from "lodash";

interface Tab {
  code: string;
  name: string;
  touched: boolean;
  formGroup?: FormGroup | undefined;
  invalidProperty: string;
  feedbackControl?: FormControl;
  redemptionControl?: FormControl;
  icon?: string;
  disabled: boolean;
}

@Component({
  selector: "lhpe-broadcast",
  templateUrl: "./broadcast.component.html",
  styleUrls: ["./broadcast.component.scss"],
})
export class BroadcastComponent implements OnInit {
  broadcast: any;
  newBroadcast!: boolean;
  broadcastFormGroup!: FormGroup;
  audiences!: any[];
  createAudienceForm!: FormGroup;
  audienceSelected = false;
  broadCastChannelSelected = false;
  broadcastChannelOptions!: { name: string; code: string }[];
  mobilePushFormGroup!: FormGroup;
  emailOptions!: { name: string }[];
  templateOptions!: any[];
  emailFormGroup!: FormGroup;
  trackingCategoryOptions!: any[];
  smsFormGroup!: FormGroup;
  browserPushFormGroup!: FormGroup;
  tagSearchValue: string = "";
  tags!: string[];
  filteredTags: string[] = [];
  selectedTags: string[] = [];
  nonSelectedTags: string[] = [];
  locations!: { name: string }[];
  selectedLocations: any[] = [];
  tagsFormGroup!: FormGroup;
  conversionsOptions!: any[];
  timeOptions!: string[];
  hourFormatOptions: string[] = ["am", "pm"];
  offerOptions!: any[];
  audienceCount: any;
  conversionTrackingOptions!: (
    | { name: string; code: null }
    | { name: string; code: string }
  )[];
  removeDateForm!: FormGroup;
  sendDateForm!: FormGroup;
  geofences: any;
  beaconRegions: any;
  initialAudience: any;
  hasTemplatesWrite: boolean = false;
  hasGeofencesList: boolean = false;
  hasBeaconRegionsList: boolean = false;
  hasOffer!: boolean;
  intervalOptions!: string[];
  offers!: any[];
  offerControl!: FormControl;
  offerFields: any;
  hasPayloadForm: boolean = false;
  payloadLookups?: PayloadLookup[];
  lookupKeys?: any[];
  payloadProperties?: FlowPayloadProperty[];
  private onComponentDestroy = new Subject();
  hasBeenSent = false;

  contentAssistantIntegration: any;
  contentTabs!: Tab[];
  currentContentTab!: Tab;
  tempForm?: FormGroup;

  bodyContent: string = "";

  constructor(
    private activatedRoute: ActivatedRoute,
    private TitleService: TitleService,
    private Router: Router,
    private AppService: AppService,
    private SessionService: SessionService,
    private SignalsService: SignalsService,
    private Dialog: MatDialog,
    private BroadcastService: BroadcastService,
    private NotificationService: NotificationService,
    private uiService: UiService,
    private FlowsService: FlowsService,
  ) {}

  ngOnInit(): void {

    this.audiences = this.activatedRoute.snapshot.data.audiences.records ?? [];

    this.contentAssistantIntegration = find(this.activatedRoute.snapshot.data.integrations, (integration: any) => integration.provider.type === 'signal_content_assistant');

    if(isObject(this.activatedRoute.snapshot.data.initialAudience)){
      this.initialAudience = this.activatedRoute.snapshot.data.initialAudience;
    } else if (isNumber(this.activatedRoute.snapshot.data.initialAudience)) {
      this.initialAudience = find(this.audiences, {
        id: this.activatedRoute.snapshot.data.initialAudience
      });
    }
    

    this.broadcast = this.activatedRoute.snapshot.data.broadcast;

    if (this.broadcast) {
      const currDateTime = new Date().getTime();
      const broadcastSentDateTime = new Date(
        this.broadcast.send_date_time,
      ).getTime();

      this.hasBeenSent =
        !this.broadcast.scheduled || currDateTime >= broadcastSentDateTime;
    }

    //Add in custom filter audience from 'master roster send broadcast to list'
    if (
      this.initialAudience?.customFilter ||
      this.initialAudience?.single_customer_qs
    ) {
      this.audiences.push(this.initialAudience);
    }

    this.templateOptions =
      this.activatedRoute.snapshot.data.signalTemplates.records ?? [];

    this.trackingCategoryOptions =
      this.activatedRoute.snapshot.data.signalCategories ?? [];

    this.offerOptions =
      this.activatedRoute.snapshot.data.accountApp.offers ?? [];

    this.tags = this.activatedRoute.snapshot.data.tags.tags ?? [];

    this.geofences = this.activatedRoute.snapshot.data.geofences
      ? this.activatedRoute.snapshot.data.geofences.records.map(
          (geofence: any) => {
            return {
              ...geofence,
              type: "geofence",
            };
          },
        )
      : [];
    this.beaconRegions = this.activatedRoute.snapshot.data.beaconRegions
      ? this.activatedRoute.snapshot.data.beaconRegions.records.map(
          (geofence: any) => {
            return {
              ...geofence,
              type: "beacon-region",
            };
          },
        )
      : [];

    this.locations = [...this.geofences, ...this.beaconRegions].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    this.nonSelectedTags = this.tags;

    this.conversionsOptions = [];

    this.newBroadcast = !this.broadcast;

    this.hasOffer = !!this.SessionService.currentAppValue!.offers?.length;

    this.hasPayloadForm =
      this.SessionService.currentAccountValue!.catalog?.signals?.payload?.display_form;

    this.initForms();

    if (!this.newBroadcast) {
      this.TitleService.setTitle(
        this.TitleService.getTitle() + ` | ${this.broadcast.name}`,
      );
    }

    if (this.initialAudience) {
      this.getCount();
    }

    this.hasTemplatesWrite =
      this.activatedRoute.snapshot.data.permissions.hasTemplatesWrite;
    this.hasGeofencesList =
      this.activatedRoute.snapshot.data.permissions.hasGeofencesList;
    this.hasBeaconRegionsList =
      this.activatedRoute.snapshot.data.permissions.hasBeaconRegionsList;

      this.contentTabs = [
        {
          code: "content",
          name: "Edit Content",
          touched: true,
          formGroup: this.mobilePushFormGroup,
          invalidProperty: "contentInvalid",
          disabled: false,
        },
        {
          code: "assistant",
          name: "CorE",
          icon: "core-dark",
          touched: false,
          formGroup: this.mobilePushFormGroup,
          invalidProperty: "assistantInvalid",
          disabled: true,
        },
      ]
  
      this.currentContentTab = this.contentTabs[0];

      this.mobilePushFormGroup.valueChanges.subscribe((change) => {
        //console.log('Push Form Update', change);
        if(change.template !== null && change.template !== undefined && this.contentTabs.length > 1){
          this.contentTabs[1].disabled = false;
        }
      });

      this.emailFormGroup.valueChanges.subscribe((change) => {
        //console.log(change);
        if(change.template !== null && this.contentTabs.length > 1){
          this.contentTabs[1].disabled = false;
        }
      });

      this.browserPushFormGroup.valueChanges.subscribe((change) => {
        //console.log(change);
      });

      this.smsFormGroup.valueChanges.subscribe((change) => {
        //console.log(change);
        if(change.template !== null && this.contentTabs.length > 1){
          this.contentTabs[1].disabled = false;
        }
      });

  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initForms() {

    //console.log('Init Forms');

    this.conversionTrackingOptions = [
      { name: "None", code: null },
      { name: "Day 1", code: "1_days" },
      { name: "Day 2", code: "2_days" },
      { name: "Day 3", code: "3_days" },
      { name: "Day 4", code: "4_days" },
      { name: "Day 5", code: "5_days" },
      { name: "Day 6", code: "6_days" },
      { name: "Day 7", code: "7_days" },
      { name: "Week 2", code: "2_weeks" },
      { name: "Week 3", code: "3_weeks" },
      { name: "Week 4", code: "4_weeks" },
    ];

    this.timeOptions = [
      "01:00",
      "01:30",
      "02:00",
      "02:30",
      "03:00",
      "03:30",
      "04:00",
      "04:30",
      "05:00",
      "05:30",
      "06:00",
      "06:30",
      "07:00",
      "07:30",
      "08:00",
      "08:30",
      "09:00",
      "09:30",
      "10:00",
      "10:30",
      "11:00",
      "11:30",
      "12:00",
      "12:30",
    ];

    //TODO REMOVE EMAIL AND OTHER NON PUSH OPTIONS FOR NOW
    this.emailOptions = [
      { name: "Email 1" },
      { name: "Email 2" },
      { name: "Email 3" },
      { name: "Email 4" },
      { name: "Email 5" },
    ];

    this.broadcastChannelOptions = [
      { name: "Mobile push notification", code: "mobilePush" },
      // { name: "Email", code: "email" },
      // { name: "SMS", code: "sms" },
      // { name: "Browser push notification", code: "browserPush" },
    ];

    //Get broadcast values
    let audience,
      broadcastChannel,
      mobileTemplate,
      mobileCategory,
      mobileOffer,
      sendAtDate,
      sendAtTime,
      sendAtFormat,
      removeAtDate,
      removeAtTime,
      removeAtFormat;

    const conversionLocations = [];
    if (!this.newBroadcast) {
      if (this.broadcast.filter_parameters?.id) {
        audience = this.audiences.find((audience) => {
          return audience.id === this.broadcast.filter_parameters?.id;
        });
      } else {
        const customAudience = {
          name: "Custom Audience",
          id: 0,
        };

        this.audiences.push(customAudience);
        audience = customAudience;
      }

      broadcastChannel = "mobilePush";

      for (let tag of this.broadcast?.tags) {
        this.tagSelected(tag);
      }

      for (let beaconRegion of this.broadcast?.conversion_beacon_regions) {
        const bcRegionObj = this.beaconRegions.find(
          (br: any) => br.id === beaconRegion,
        );
        conversionLocations.push(bcRegionObj?.name);

        this.selectedLocations.push(bcRegionObj);
      }
      for (let geofence of this.broadcast?.conversion_geofences) {
        const geofenceObj = this.geofences.find(
          (gf: any) => gf.id === geofence,
        );
        conversionLocations.push(geofenceObj?.name);

        this.selectedLocations.push(geofenceObj);
      }

      if (this.broadcast?.send_date_time) {
        const parsed = this.parseDateTimeForForm(this.broadcast.send_date_time);
        sendAtDate = parsed.date;
        sendAtTime = parsed.time;
        sendAtFormat = parsed.format;
      }

      if (this.broadcast?.removal_date_time) {
        const parsed = this.parseDateTimeForForm(
          this.broadcast.removal_date_time,
        );
        removeAtDate = parsed.date;
        removeAtTime = parsed.time;
        removeAtFormat = parsed.format;
      }

      if (broadcastChannel === "mobilePush") {
        mobileTemplate = this.templateOptions.find(
          (template) => template.id === this.broadcast?.signalTemplate.id,
        );
        mobileCategory = this.trackingCategoryOptions.find(
          (category) => category.id === this.broadcast?.signalCategory.id,
        );
        mobileOffer = this.offerOptions.find(
          (offer) => offer.id === this.broadcast?.account_app_offer_id,
        );
      }
    } else {
      if (this.initialAudience) {
        audience = this.audiences.find(
          (audience) => audience.id === this.initialAudience.id,
        );
      }
    }

    this.broadcastFormGroup = new FormGroup({
      audience: new FormControl(
        { value: audience, disabled: this.hasBeenSent },
        [Validators.required],
      ),
      broadcastChannel: new FormControl(
        { value: broadcastChannel, disabled: this.hasBeenSent },
        [Validators.required],
      ),
    });

    this.mobilePushFormGroup = new FormGroup({
      broadcastName: new FormControl(
        { value: this.broadcast?.name, disabled: this.hasBeenSent },
        [Validators.required],
      ),
      template: new FormControl(
        { value: mobileTemplate, disabled: this.hasBeenSent },
        [Validators.required],
      ),
      category: new FormControl(
        { value: mobileCategory, disabled: this.hasBeenSent },
        [Validators.required],
      ),
      notificationHeadline: new FormControl(
        {
          value: this.broadcast?.push_notification_headline,
          disabled: this.hasBeenSent,
        },
        [Validators.required],
      ),
      notificationText: new FormControl(
        {
          value: this.broadcast?.push_notification_text,
          disabled: this.hasBeenSent,
        },
        [Validators.required],
      ),
      bodyContent: new FormControl(
        {
          value: this.broadcast?.push_notification_content,
          disabled: this.hasBeenSent,
        },
        [Validators.required],
      ),
    });

    this.emailFormGroup = new FormGroup({
      email: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
        Validators.email,
      ]),
      broadcastName: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      template: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
      trackingCategory: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
    });

    this.smsFormGroup = new FormGroup({
      broadcastName: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      trackingCategory: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      textLabel: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
      messageBody: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
      link: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
    });

    this.browserPushFormGroup = new FormGroup({
      broadcastName: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      trackingCategory: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      notificationHeadline: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      notificationBody: new FormControl(
        { value: "", disabled: this.hasBeenSent },
        [Validators.required],
      ),
      actionLabel: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
      actionLink: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
    });

    this.createAudienceForm = new FormGroup({
      audienceName: new FormControl({ value: "", disabled: this.hasBeenSent }, [
        Validators.required,
      ]),
    });

    this.tagsFormGroup = new FormGroup({
      locations: new FormControl({
        value: conversionLocations,
        disabled: this.hasBeenSent,
      }),
      conversions: new FormControl({
        value: this.broadcast?.conversion_length,
        disabled: this.hasBeenSent,
      }),
      redemption: new FormControl({
        value:
          this.broadcast?.has_redemption !== undefined ||
          this.broadcast?.has_redemption !== null
            ? this.broadcast?.has_redemption
            : mobileTemplate.has_redemption,
        disabled: this.hasBeenSent,
      }),
      feedback: new FormControl({
        value:
          this.broadcast?.has_feedback !== undefined ||
          this.broadcast?.has_feedback !== null
            ? this.broadcast?.has_feedback
            : mobileTemplate.has_feedback,
        disabled: this.hasBeenSent,
      }),
    });

    this.sendDateForm = new FormGroup({
      sendAtDate: new FormControl(
        { value: sendAtDate, disabled: this.hasBeenSent },
        [Validators.required],
      ),
      sendAtTime: new FormControl(
        { value: sendAtTime, disabled: this.hasBeenSent },
        [Validators.required],
      ),
      sendAtTimeFormat: new FormControl(
        { value: sendAtFormat, disabled: this.hasBeenSent },
        [Validators.required],
      ),
    });

    this.removeDateForm = new FormGroup({
      removeAtDate: new FormControl(
        { value: removeAtDate, disabled: this.hasBeenSent },
        RequiredIfFormHasAnyValues(),
      ),
      removeAtTime: new FormControl(
        { value: removeAtTime, disabled: this.hasBeenSent },
        RequiredIfFormHasAnyValues(),
      ),
      removeAtTimeFormat: new FormControl(
        { value: removeAtFormat, disabled: this.hasBeenSent },
        RequiredIfFormHasAnyValues(),
      ),
    });

    //Manually set propertys of broadcast
    if (!this.newBroadcast) {
      if (audience) {
        this.audienceSelected = true;
        this.getCount();
      }
      if (broadcastChannel) {
        this.broadCastChannelSelected = true;
      }
    } else {
      if (audience) {
        this.audienceSelected = true;
      }
    }

    this.removeDateForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        this.removeDateForm.controls.removeAtDate.updateValueAndValidity({
          emitEvent: false,
        });
        this.removeDateForm.controls.removeAtTime.updateValueAndValidity({
          emitEvent: false,
        });
        this.removeDateForm.controls.removeAtTimeFormat.updateValueAndValidity({
          emitEvent: false,
        });
      });

    this.tagsFormGroup.controls.locations.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        this.selectedLocations = value.map((locationName: string) => {
          return this.locations.find(
            (location) => location.name === locationName,
          );
        });
      });

    this.broadcastFormGroup.controls.audience.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (value) {
          this.audienceSelected = true;
          this.getCount();
        }
      });

    this.broadcastFormGroup.controls.broadcastChannel.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (value) {
          this.broadCastChannelSelected = true;
        }
      });

    this.bodyContent = this.broadcast?.push_notification_content

    if (this.hasPayloadForm) {
      this.initPayload();
    }

    if (this.hasOffer) {
      this.initOffer();
    }
  }

  initPayload() {
    this.payloadLookups =
      this.SessionService.currentAccountValue!.catalog.signals.payload.lookups;

    this.lookupKeys = this.getLookupKeyNames();

    this.payloadProperties = [];

    if (this.broadcast?.push_notification_payload) {
      for (let payloadKey of Object.keys(
        this.broadcast.push_notification_payload,
      )) {
        this.payloadProperties.push({
          keyControl: new FormControl({ value: payloadKey, disabled: true }, [
            Validators.required,
          ]),
          valueControl: new FormControl(
            {
              value: this.broadcast.push_notification_payload[payloadKey],
              disabled: true,
            },
            [Validators.required],
          ),
          completed: true,
          keyNameIsLookup: false,
          keyNamePrefix: "Enter",
          valueAutoCompleteOptions: [],
        });
      }
    }

    this.pushNewPayloadProperty();
  }

  initOffer() {
    this.intervalOptions = ["hours", "days", "months", "years"];

    this.offers = [
      { name: "Do Not Send Offer", id: null },
      ...this.SessionService.currentAppValue!.offers,
    ];
    this.offerControl = new FormControl({
      value: null,
      disabled: this.hasBeenSent,
    });

    this.mobilePushFormGroup.addControl("offerControl", this.offerControl);

    this.offerControl.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (!!change.id) {
          this.offerFields = change.config.fields.map((field: any) => {
            const offerField = {
              ...field,
              control: new FormControl(),
            };
            if (field.required) {
              offerField.control.setValidators([Validators.required]);
            }

            if (this.hasBeenSent) {
              offerField.control.disable();
            }

            if (field.type === "Interval") {
              offerField.intervalControl = new FormControl(null, [
                Validators.required,
              ]);
            }

            if (field.type === "Date") {
              offerField.timeString = null;
            }

            return offerField;
          });
          for (let offerField of this.offerFields) {
            if (offerField.type === "Select") {
              offerField.control.valueChanges
                .pipe(takeUntil(this.onComponentDestroy))
                .subscribe((change: any) => {
                  if (change.hides) {
                    for (let field of change.hides) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.hidden = true;
                      }
                    }
                  }

                  if (change.shows) {
                    for (let field of change.shows) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.hidden = false;
                      }
                    }
                  }

                  if (change.requires) {
                    for (let field of change.requires) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.required = true;
                        offerField.control.setValidators([Validators.required]);
                      }
                    }
                  }

                  if (change.notrequires) {
                    for (let field of change.notrequires) {
                      const offerField = this.offerFields.find(
                        (offerField: any) => offerField.id === field,
                      );
                      if (offerField) {
                        offerField.required = false;
                        offerField.control.clearValidators();
                      }
                    }
                  }
                });
            }

            if (
              this.broadcast?.account_app_offer_id &&
              this.broadcast?.account_app_offer_data[offerField.id]
            ) {
              if (offerField.type === "Select") {
                const option = offerField.options.find(
                  (option: any) =>
                    option.value ===
                    this.broadcast?.account_app_offer_data[offerField.id].value,
                );
                if (option) {
                  offerField.control.setValue(option);
                }
              } else if (offerField.type === "Interval") {
                offerField.control.setValue(
                  this.broadcast?.account_app_offer_data[offerField.id].count,
                );
                offerField.intervalControl.setValue(
                  this.broadcast?.account_app_offer_data[offerField.id].period,
                );
              } else if (offerField.type === "Date") {
                offerField.control.setValue(
                  this.broadcast?.account_app_offer_data[offerField.id],
                );

                if (this.broadcast?.account_app_offer_data[offerField.id]) {
                  offerField.timeString = moment(
                    this.broadcast?.account_app_offer_data[offerField.id],
                  ).format("hh:mm A");
                }
              } else {
                offerField.control.setValue(
                  this.broadcast?.account_app_offer_data[offerField.id],
                );
              }
            }
          }
        } else {
          this.offerFields = [];
        }
      });

    if (this.broadcast?.account_app_offer_id) {
      const offer = this.offers.find(
        (offer) => offer.id === this.broadcast?.account_app_offer_id,
      );
      this.offerControl.setValue(offer);
    }
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get currentApp() {
    return this.SessionService.currentAppValue!;
  }

  get currentUser() {
    return this.SessionService.currentUserValue!;
  }

  get facilities() {
    return this.SessionService.facilitiesValue!;
  }

  get filterDTO() {
    var audienceDTO = { ...this.broadcastFormGroup.controls.audience.value };
    if(this.broadcastFormGroup.controls.audience.value.id == 0 && this.broadcast.filter_parameters !== null){
      audienceDTO = { ...this.broadcast.filter_parameters }
    }
    delete audienceDTO.name;
    return audienceDTO;
  }

  get filterObj() {
    return {
      facility_id: this.facilities.map((facility) => facility.id),
      filter: this.filterDTO,
    };
  }

  get mobilePushSelected() {
    return (
      this.broadcastFormGroup.controls.broadcastChannel.value === "mobilePush"
    );
  }

  get saveDisabled() {
    let braodcastFormInvalid = false;
    if (this.mobilePushSelected) {
      braodcastFormInvalid = this.mobilePushFormGroup.invalid;
    }
    return (
      this.broadcastFormGroup.invalid ||
      braodcastFormInvalid ||
      this.sendDateForm.invalid ||
      this.removeDateForm.invalid ||
      this.offerInvalid
    );
  }

  get broadcastDTO() {
    let broadcastObj = {};
    if (this.mobilePushSelected) {
      broadcastObj = {
        name: this.mobilePushFormGroup.controls.broadcastName.value,
        template: this.mobilePushFormGroup.controls.template.value,
        category_id: this.mobilePushFormGroup.controls.category.value.id,
        title: this.mobilePushFormGroup.controls.notificationHeadline.value,
        preview: this.mobilePushFormGroup.controls.notificationText.value,
        body: this.mobilePushFormGroup.controls.bodyContent.value,
        has_feedback: this.tagsFormGroup.controls.feedback.value,
        has_redemption: this.tagsFormGroup.controls.redemption.value,
        customer_search_qs: true,
        account_app_offer_id: this.accountAppOfferID,
        account_app_offer_data: this.offerDataDTO,
        payload: this.payloadDTO,
        account_app_audience_id:
          this.broadcastFormGroup.controls.audience.value.id,

        // account_app_offer_id: this.mobilePushFormGroup.controls.offer.value.id,
        // account_app_offer_data: this.mobilePushFormGroup.controls.offer.value,
      };
    }

    return {
      id: this.broadcast?.id ?? undefined,
      account_app_id: this.currentApp.id,
      search: {
        ...this.broadcastFormGroup.controls.audience.value,
        facility_id: this.facilities.map((facility) => facility.id),
        name: "",
      },
      tags: this.selectedTags,
      conversionLocations: this.selectedLocations.map((location) => {
        return { type: location.type, id: location.id };
      }),
      conversionLength: this.tagsFormGroup.controls.conversions.value,
      payload: {},
      send_date_time: this.getSendDateTimeIso(),
      removal_date_time: this.getRemovalDateTimeIso(),
      user_id: this.currentUser.id,
      account_id: this.currentAccount.accountID,
      facilities: this.facilities.map((facility) => facility.id),
      scheduled: true,
      ...broadcastObj,
    };
  }

  get redemptionControl() {
    return this.tagsFormGroup!.controls.redemption as FormControl;
  }

  get feedbackControl() {
    return this.tagsFormGroup!.controls.feedback as FormControl;
  }

  get offerDataDTO() {
    const obj: any = {};
    if (this.hasOffer) {
      const nonHiddenFields = this.offerFields.filter(
        (field: any) => !field.hidden,
      );
      for (let offerField of nonHiddenFields) {
        if (offerField.type === "Interval") {
          obj[offerField.id] = {
            period: offerField.intervalControl.value,
            count: offerField.control.value,
          };
        } else if (offerField.type === "Date") {
          const dateTime = new Date(offerField.control.value);

          const [hour, minutes] = moment(offerField.timeString, "hh:mm A")
            .format("HH:mm")
            .split(":");

          dateTime.setHours(Number.parseInt(hour));
          dateTime.setMinutes(Number.parseInt(minutes));
          obj[offerField.id] = dateTime.toISOString();
        } else if (offerField.type === "Integer") {
          obj[offerField.id] = Number.parseInt(offerField.control.value);
        } else {
          obj[offerField.id] = offerField.control.value;
        }
      }
    }

    return obj;
  }

  get accountAppOfferID() {
    if (this.hasOffer) {
      return this.offerControl?.value.id;
    }

    return null;
  }

  get payloadDTO() {
    const obj: any = {};

    if (this.hasPayloadForm) {
      const completedProperties = this.payloadProperties!.filter(
        (property) => property.completed,
      );

      for (let property of completedProperties) {
        obj[property.keyControl.value] = property.valueControl.value;
      }
    }

    return obj;
  }

  get offerInvalid() {
    if (this.hasOffer && this.offerFields) {
      const nonHiddenFields = this.offerFields.filter(
        (field: any) => !field.hidden,
      );
      for (let offerField of nonHiddenFields) {
        if (offerField.type === "Interval") {
          if (
            offerField.control.invalid ||
            offerField.intervalControl.invalid
          ) {
            return true;
          }
        } else if (offerField.type === "Date") {
          if (offerField.control.invalid || !offerField.timeString) {
            return true;
          }
        } else {
          if (offerField.control.invalid) {
            return true;
          }
        }
      }
    }
    return false;
  }

  get hasCompletedProperty() {
    return !!this.payloadProperties?.find((property) => property.completed);
  }

  addPayloadProperty(currentProperty: FlowPayloadProperty) {
    currentProperty.completed = true;
    currentProperty.keyControl.disable();
    currentProperty.valueControl.disable();
    this.pushNewPayloadProperty();
  }

  removePayloadProperty(currentProperty: FlowPayloadProperty) {
    this.payloadProperties = this.payloadProperties!.filter(
      (property) => property !== currentProperty,
    );
  }

  pushNewPayloadProperty() {
    const payLoadProperty: FlowPayloadProperty = {
      keyControl: new FormControl("", [Validators.required]),
      valueControl: new FormControl("", [Validators.required]),
      completed: false,
      keyNameIsLookup: false,
      keyNamePrefix: "Enter",
      valueAutoCompleteOptions: [],
    };
    payLoadProperty.keyControl.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (this.lookupKeys?.includes(value)) {
          payLoadProperty.keyNameIsLookup = true;
          payLoadProperty.keyNamePrefix = "Search";
        } else {
          if (payLoadProperty.keyNameIsLookup) {
            payLoadProperty.keyNameIsLookup = false;
            payLoadProperty.keyNamePrefix = "Enter";
          }
        }
      });

    payLoadProperty.valueControl.valueChanges
      .pipe(
        startWith([]),
        filter(
          (value) =>
            payLoadProperty.keyNameIsLookup && value && value.length > 2,
        ),
        debounceTime(300),
        switchMap((value) =>
          this.FlowsService.getPayloadLookup(
            this.SessionService.currentAccountValue!.accountID,
            payLoadProperty.keyControl.value,
            value,
          ),
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res: any) => {
        payLoadProperty.valueAutoCompleteOptions = res;
      });

    this.payloadProperties!.push(payLoadProperty);
  }

  onTimePickerTimeChanged(time: string, field: any) {
    field.timeString = time;
  }

  applyTagFilter() {
    if (!this.tagSearchValue || this.tagSearchValue === "") {
      this.filteredTags = [];
    } else {
      this.filteredTags = this.nonSelectedTags.filter((tag) =>
        tag.toLowerCase().includes(this.tagSearchValue.toLowerCase()),
      );
    }
  }

  tagSelected(tag: any) {
    this.selectedTags.push(tag);
    this.nonSelectedTags = this.nonSelectedTags.filter(
      (nonSelectedTag) => nonSelectedTag !== tag,
    );
    this.applyTagFilter();
  }

  tagRemoved(tag: any) {
    this.selectedTags = this.selectedTags.filter(
      (selectTag) => selectTag !== tag,
    );
    this.nonSelectedTags.push(tag);
    this.applyTagFilter();
  }

  createTag() {
    const newTag = this.tagSearchValue;
    this.tagSearchValue = "";
    this.tags.push(newTag);
    this.selectedTags.push(newTag);
  }

  locationRemoved(removedLocation: any) {
    const locations = this.tagsFormGroup.controls.locations.value.filter(
      (location: any) => location !== removedLocation.name,
    );
    this.tagsFormGroup.controls.locations.setValue(locations);
  }

  contentTabChanged(tab: any) {
    if(!tab.disabled) {
      this.currentContentTab = tab; 
      this.currentContentTab.touched = true;
    } else {
      this.mobilePushFormGroup!.markAllAsTouched();
    }
  }

  saveNewBroadcast() {
    this.uiService
      .waitForEvent(
        this.BroadcastService.createBroadcast(
          this.currentAccount.accountID,
          this.currentApp.id,
          this.broadcastDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.NotificationService.openSuccessSnackBar("Broadcast created");
          this.Router.navigate(["broadcasts", "list"]);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error creating Broadcast",
          );
        },
      );
  }

  saveBroadcastUpdate() {
    this.BroadcastService.updateBroadcast(
      this.currentAccount.accountID,
      this.currentApp.id,
      this.broadcastDTO,
    )
      .pipe(first())
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar("Broadcast updated");
          this.Router.navigate(["broadcasts", "list"]);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error updating Broadcast",
          );
        },
      );
  }

  deleteBroadcast() {}

  onViewAudiences() {
    this.Router.navigate(["broadcasts", "audiences", "list"]);
  }

  onCreateNewAudience() {
    this.Router.navigate(["broadcasts", "audiences", "create"]);
  }

  getLookupKeyNames() {
    return (
      this.payloadLookups?.map(
        (payloadLookup) => (payloadLookup as any)[payloadLookup.label_path],
      ) ?? []
    );
  }

  getCount() {
    this.uiService
      .waitForEvent(
        this.AppService.getFilterCount(
          this.currentAccount.accountID,
          this.currentApp.id,
          this.filterObj,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res: any) => {
          this.audienceCount = res.count;
        },
        (err) => {
          this.audienceCount = "n/a";
        },
      );
  }

  bodyContentChanged(event: any) {
    this.mobilePushFormGroup.controls.bodyContent.setValue(event);
  }

  preview(type: string, args?: any) {

    const previewDTO = {
      ...this.mobilePushFormGroup.controls.template.value,
      has_feedback: this.tagsFormGroup.controls.feedback.value,
      has_redemption: this.tagsFormGroup.controls.redemption.value,
      headline: this.mobilePushFormGroup.controls.notificationHeadline.value,
      notification: this.mobilePushFormGroup.controls.notificationText.value,
      content: this.mobilePushFormGroup.controls.bodyContent.value,
    };

    this.uiService
      .waitForEvent(
        this.SignalsService.getSignalPreview(
          this.currentAccount.accountID,
          previewDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        const resTemplate = (res as unknown as any).template;
        let data = {
          type,
          preview: resTemplate,
          templateOptions: this.templateOptions,
          templateControl: this.mobilePushFormGroup.controls.template,
          headlineControl:
            this.mobilePushFormGroup.controls.notificationHeadline,
          notificationControl:
            this.mobilePushFormGroup.controls.notificationText,
          contentControl: this.mobilePushFormGroup.controls.bodyContent,
          feedbackControl: this.tagsFormGroup.controls.feedback,
          redemptionControl: this.tagsFormGroup.controls.redemption,
          forAssistant: false,
        };
        if(args !== undefined){
          this.tempForm = new FormGroup({
            headlineControl: new FormControl(args.data.data.arguments.headline),
            notificationControl: new FormControl(args.data.data.arguments.push_notification),
            contentControl: new FormControl(args.data.data.arguments.body),
          });
          data.headlineControl = this.tempForm.controls.headlineControl;
          data.notificationControl = this.tempForm.controls.notificationControl;
          data.contentControl = this.tempForm.controls.contentControl;
          data.forAssistant = true;
        }
        const dialogRef = this.Dialog.open(SignalPreviewDialogComponent, {
          data: data,
          disableClose: args !== undefined,
        });
        dialogRef.afterClosed().subscribe((result) => {
          //console.log('phone node preview result', result);
          if(args === undefined){
            return;
          }
          if(result === false){
            args.resultFn({accepted: false});
          } else if (result === true) {
            this.mobilePushFormGroup.controls.notificationHeadline.setValue(this.tempForm!.controls.headlineControl.value);
            this.mobilePushFormGroup.controls.notificationText.setValue(this.tempForm!.controls.notificationControl.value);
            this.mobilePushFormGroup.controls.bodyContent.setValue(this.tempForm!.controls.contentControl.value);
            args.resultFn({accepted: true});
          }
        });
      });
  }

  goToNewTemplate() {
    this.Router.navigate(["content", "template", "create"]);
  }

  get contentAssistantData() {
    return {
      headline: this.mobilePushFormGroup.controls.notificationHeadline.value,
      notification: this.mobilePushFormGroup.controls.notificationText.value,
      body: this.mobilePushFormGroup.controls.bodyContent.value,
    }
  }

  endAssistant() {
    this.currentContentTab = this.contentTabs[0];
  }

  private get userTimezone(): string {
    return this.SessionService.currentUserValue!.timezone;
  }

  private parseDateTimeForForm(isoString: string): {
    date: Date;
    time: string;
    format: "am" | "pm";
  } {
    const m = moment(isoString).tz(this.userTimezone);
    const hours24 = m.hours();
    const minutes = m.minutes();
    const minuteString = minutes < 30 ? "00" : "30";
    let hours12 = hours24 % 12;
    if (hours12 === 0) {
      hours12 = 12;
    }
    const format: "am" | "pm" = hours24 >= 12 ? "pm" : "am";
    const hourString =
      hours12 < 10 ? `0${hours12}` : hours12.toString();

    return {
      date: new Date(m.year(), m.month(), m.date()),
      time: `${hourString}:${minuteString}`,
      format,
    };
  }

  private combineFormDateTime(
    date: Date,
    time: string,
    meridiem: string,
  ): string {
    let [hour, minutes] = time.split(":").map((v) => Number.parseInt(v, 10));
    if (meridiem === "pm" && hour !== 12) {
      hour += 12;
    }
    if (meridiem === "am" && hour === 12) {
      hour = 0;
    }

    return moment
      .tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          day: date.getDate(),
          hour,
          minute: minutes,
        },
        this.userTimezone,
      )
      .toISOString();
  }

  private getSendDateTimeIso(): string {
    return this.combineFormDateTime(
      this.sendDateForm.controls.sendAtDate.value,
      this.sendDateForm.controls.sendAtTime.value,
      this.sendDateForm.controls.sendAtTimeFormat.value,
    );
  }

  private getRemovalDateTimeIso(): string | null {
    const date = this.removeDateForm.controls.removeAtDate.value;
    if (!date) {
      return null;
    }

    return this.combineFormDateTime(
      date,
      this.removeDateForm.controls.removeAtTime.value,
      this.removeDateForm.controls.removeAtTimeFormat.value,
    );
  }
}

export function RequiredIfFormHasAnyValues(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (
      control!.value === "" ||
      control!.value === undefined ||
      control!.value === null
    ) {
      const formGroup = control.parent;

      if (formGroup?.controls) {
        for (let key of Object.keys(formGroup.controls)) {
          const formControl = formGroup.get(key);

          if (control !== formControl) {
            if (
              !(
                formControl!.value === "" ||
                formControl!.value === undefined ||
                formControl!.value === null
              )
            ) {
              return { required: { message: "This field is required" } };
            }
          }
        }
      }
    }

    return null;
  };
}
